import React, { FC, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { Button, Paragraph } from '@pandiafi/design-system'
import { ContextNotification, DetailRow } from '@pandiafi/components'

import {
  Contract,
  ContractTenant,
  ModifyContractInput,
  ResidentAppLocale,
  UserMetadata,
} from '../../../../common/validations'
import i18n from '../../../i18n/i18n'
import { useAPIConfirmCreateModification } from '../../../api/apiHooks/useAPIConfirmCreateModification'
import { Section } from '../../../components'
import { getTenantName } from '../../../utils/getTenantName'
import { renderDate } from '../../../utils'

import { CardTitleBand } from 'components/CardTitleBand/CardTitleBand'

type Props = {
  formValues: ModifyContractInput
  tenants: ContractTenant[]
  contract: Contract
  cancel: () => void
  userMetadata: UserMetadata
}

export const ModificationPreview: FC<Props> = ({
  formValues,
  tenants,
  contract,
  cancel,
  userMetadata: { identityProvider, isImpersonator },
}) => {
  const { t } = useTranslation(['common', 'contractModification'])

  const lang = i18n.language ?? ResidentAppLocale.FI

  const { mutate, isSuccess, data, isPending } =
    useAPIConfirmCreateModification(contract.uuid, lang)

  useEffect(() => {
    if (isSuccess && data && data.link) {
      window.location.href = data.link
    }
  })

  const handleCancel = () => {
    cancel()
  }

  const handleSubmit = () => {
    mutate({
      operationData: formValues,
      provider: identityProvider,
    })
  }

  const terminatedTenants = tenants.filter((tenant) =>
    formValues.removeTenants?.some(
      (removeTenant) => removeTenant.tenantId === tenant.id
    )
  )

  const newContractParty = tenants.find(
    (tenant) => tenant.id === formValues.transfer?.destinationTenantId
  )

  // We want to show the loading spinner until the user is redirected
  const isLoading = isPending || isSuccess

  return (
    <>
      <CardTitleBand>
        {t('contractModification:headings.modificationPreview')}
      </CardTitleBand>
      <Section className="mb-12">
        <ContextNotification textAlign="center">
          <div className="space-y-2">
            <Paragraph>
              {t('contractModification:texts.confirmationInstruction')}
            </Paragraph>
            <Paragraph>
              <strong>
                {t('contractModification:texts.returnFromAuthReminder')}
              </strong>
            </Paragraph>
          </div>
        </ContextNotification>
      </Section>

      <Section className="mb-12">
        <DetailRow>
          {terminatedTenants.length > 0 && (
            <DetailRow.Item
              title={t('contractModification:labels.movingOutTenant')}
            >
              {terminatedTenants.map((tenant) => (
                <span key={tenant.id}>{getTenantName(tenant)}</span>
              ))}
            </DetailRow.Item>
          )}
          <DetailRow.Item
            title={t('contractModification:labels.movingOutDate')}
          >
            {renderDate(formValues.inEffect)}
          </DetailRow.Item>
          {formValues.description && (
            <DetailRow.Item
              title={t('contractModification:labels.modificationDescription')}
              preLine
            >
              {formValues.description}
            </DetailRow.Item>
          )}
          {newContractParty && (
            <DetailRow.Item
              title={t('contractModification:labels.newContractParty')}
            >
              {getTenantName(newContractParty)}
            </DetailRow.Item>
          )}
        </DetailRow>
      </Section>
      <div className="min-tablet:mt-16 min-tablet:flex min-tablet:items-center min-tablet:justify-end flex gap-4">
        <Button type="button" variant="cancel" onClick={() => handleCancel()}>
          {t('contractModification:buttons.backToEdit')}
        </Button>
        <Button
          type="button"
          variant="delete"
          onClick={() => handleSubmit()}
          disabled={isImpersonator || isLoading}
          loading={isLoading}
        >
          {t('contractModification:buttons.confirmModification')}
        </Button>
      </div>
    </>
  )
}
